import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DaeunResult, DaeunEntry } from '@/utils/daeunCalculations';
import { ELEMENT_COLORS, ELEMENT_BG_COLORS } from '@/utils/sajuCalculations';

// English romanization for Heavenly Stems (天干)
const STEMS_EN = ['Jiǎ', 'Yǐ', 'Bǐng', 'Dīng', 'Wù', 'Jǐ', 'Gēng', 'Xīn', 'Rén', 'Guǐ'];
// English romanization for Earthly Branches (地支)
const BRANCHES_EN = ['Zǐ', 'Chǒu', 'Yín', 'Mǎo', 'Chén', 'Sì', 'Wǔ', 'Wèi', 'Shēn', 'Yǒu', 'Xū', 'Hài'];

interface DaeunSectionProps {
  daeun: DaeunResult;
  birthYear: number;
}

function DaeunCard({ entry, isVisible, delay }: { entry: DaeunEntry; isVisible: boolean; delay: number }) {
  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [isVisible, delay]);

  const isEn = i18n.language === 'en';
  const stemLabel = isEn ? STEMS_EN[entry.stemIdx] : entry.stemKor;
  const branchLabel = isEn ? BRANCHES_EN[entry.branchIdx] : entry.branchKor;

  const stemBg    = ELEMENT_BG_COLORS[entry.stemElement];
  const stemColor = ELEMENT_COLORS[entry.stemElement];
  const brBg      = ELEMENT_BG_COLORS[entry.branchElement];
  const brColor   = ELEMENT_COLORS[entry.branchElement];

  return (
    <div
      className="flex-none transition-all duration-500"
      style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(16px)' }}
    >
      {entry.isCurrent ? (
        <div className="relative w-32 rounded-2xl overflow-hidden border-2 border-gray-900">
          <div className="absolute top-0 left-0 right-0 bg-gray-900 text-white text-center py-1.5">
            <span className="text-xs font-bold">{t('daeun.current')}</span>
          </div>
          <div className="pt-10 pb-4 px-4 bg-white">
            <p className="text-xs text-gray-400 text-center mb-2.5">
              {entry.startAge}{t('daeun.age.range')}{entry.endAge}{t('daeun.age.unit')}
            </p>
            <div className="flex flex-col gap-2 items-center">
              <div className="w-16 h-16 rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: stemBg }}>
                <span className="text-2xl font-bold" style={{ color: stemColor, fontFamily: "'Noto Serif KR', serif" }}>{entry.stem}</span>
                <span className={`font-medium ${isEn ? 'text-[10px] leading-tight' : 'text-xs'}`} style={{ color: stemColor }}>{stemLabel}</span>
              </div>
              <div className="w-16 h-16 rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: brBg }}>
                <span className="text-2xl font-bold" style={{ color: brColor, fontFamily: "'Noto Serif KR', serif" }}>{entry.branch}</span>
                <span className={`font-medium ${isEn ? 'text-[10px] leading-tight' : 'text-xs'}`} style={{ color: brColor }}>{branchLabel}</span>
              </div>
            </div>
            <div className="mt-2.5 text-center">
              <span className="text-xs text-gray-400">{entry.startYear}{t('daeun.year.unit')}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-24 rounded-xl border border-gray-100 bg-white px-3 py-3.5 flex flex-col items-center gap-1.5">
          <p className="text-xs text-gray-400">{entry.startAge}{t('daeun.age.range')}{entry.endAge}{t('daeun.age.unit')}</p>
          <div className="w-11 h-11 rounded-lg flex flex-col items-center justify-center" style={{ backgroundColor: stemBg }}>
            <span className="text-xl font-bold" style={{ color: stemColor, fontFamily: "'Noto Serif KR', serif" }}>{entry.stem}</span>
            <span className={`${isEn ? 'text-[9px] leading-tight' : 'text-xs'}`} style={{ color: stemColor }}>{stemLabel}</span>
          </div>
          <div className="w-11 h-11 rounded-lg flex flex-col items-center justify-center" style={{ backgroundColor: brBg }}>
            <span className="text-xl font-bold" style={{ color: brColor, fontFamily: "'Noto Serif KR', serif" }}>{entry.branch}</span>
            <span className={`${isEn ? 'text-[9px] leading-tight' : 'text-xs'}`} style={{ color: brColor }}>{branchLabel}</span>
          </div>
          <p className="text-xs text-gray-300">{entry.startYear}{t('daeun.year.unit')}</p>
        </div>
      )}
    </div>
  );
}

export default function DaeunSection({ daeun, birthYear }: DaeunSectionProps) {
  const { t } = useTranslation();
  const [sectionVisible, setSectionVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setSectionVisible(true); },
      { threshold: 0.2 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!sectionVisible || daeun.currentIndex < 0) return;
    const timer = setTimeout(() => {
      const container = scrollRef.current;
      if (!container) return;
      const card = container.children[daeun.currentIndex] as HTMLElement;
      if (card) {
        container.scrollTo({ left: card.offsetLeft - 24, behavior: 'smooth' });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [sectionVisible, daeun.currentIndex]);

  const dirLabel = daeun.direction === 'forward' ? t('daeun.forward') : t('daeun.backward');
  const startText = daeun.startAge.years === 0 && daeun.startAge.months === 0
    ? t('daeun.startAge.immediate')
    : `${daeun.startAge.years > 0 ? `${daeun.startAge.years}${t('daeun.age.unit')}` : ''}${daeun.startAge.months > 0 ? ` ${daeun.startAge.months}${t('daeun.startAge.months')}` : ''}${t('daeun.startAge')}`;

  return (
    <div ref={ref} className="bg-white border border-gray-100 rounded-2xl overflow-hidden mb-6">
      <div
        className="px-5 pt-5 pb-4 transition-all duration-700"
        style={{ opacity: sectionVisible ? 1 : 0, transform: sectionVisible ? 'translateY(0)' : 'translateY(12px)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
            <i className="ri-time-line text-amber-500 text-sm" />
          </div>
          <h3 className="text-sm font-bold text-gray-900">{t('daeun.title')}</h3>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed mb-3">{t('daeun.desc')}</p>
        <div className="flex gap-2">
          <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">{dirLabel}</span>
          <span className="text-xs bg-amber-50 text-amber-700 px-3 py-1 rounded-full font-medium">{startText}</span>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 px-5 pb-5 overflow-x-auto scrollbar-hide"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {daeun.entries.map((entry, i) => (
          <div key={entry.index} style={{ scrollSnapAlign: 'start' }}>
            <DaeunCard entry={entry} isVisible={sectionVisible} delay={200 + i * 80} />
          </div>
        ))}
      </div>

      <div
        className="px-5 pb-4 transition-all duration-700"
        style={{ opacity: sectionVisible ? 1 : 0, transitionDelay: '600ms' }}
      >
        <div className="bg-amber-50 rounded-xl p-3">
          <p className="text-xs text-amber-700 leading-relaxed">
            <strong>{t('daeun.guide.title')}:</strong> {t('daeun.guide.desc')}
          </p>
        </div>
      </div>
    </div>
  );
}
